import React, { useEffect, useState } from "react";
import {
  FormActions,
  FormField,
  FormInput,
  FormModal,
  FormSelect,
  FormTextarea,
  Toggle,
  useToast,
} from "@/components/shared";
import { toolService } from "@/lib/services";
import type { ToolSummaryForExtension } from "@/types";

export interface EditToolModalProps {
  tool: ToolSummaryForExtension;
  onClose: () => void;
  onSave: () => void;
}

export function EditToolModal({ tool, onClose, onSave }: Readonly<EditToolModalProps>) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Common fields
  const [name, setName] = useState(tool.name);
  const [description, setDescription] = useState(tool.description ?? "");
  const [icon, setIcon] = useState(tool.icon);
  const [isEnabled, setIsEnabled] = useState(tool.isEnabled);

  // API endpoint fields
  const [apiUrl, setApiUrl] = useState("");
  const [apiMethod, setApiMethod] = useState("POST");
  const [headersJson, setHeadersJson] = useState("");
  const [parametersJson, setParametersJson] = useState("");
  const [responsePath, setResponsePath] = useState("");

  // MCP server fields
  const [mcpUrl, setMcpUrl] = useState("");
  const [mcpHeadersJson, setMcpHeadersJson] = useState("");
  const [mcpTimeout, setMcpTimeout] = useState<number | "">(30);

  // Validation errors
  const [headersError, setHeadersError] = useState("");
  const [parametersError, setParametersError] = useState("");
  const [mcpHeadersError, setMcpHeadersError] = useState("");

  useEffect(() => {
    const fetchTool = async () => {
      try {
        const response = await toolService.get(tool.uuid);
        const fullTool = response.data;
        const config = fullTool.config ?? {};

        setName(fullTool.name);
        setDescription(fullTool.description ?? "");
        setIcon(fullTool.icon);
        setIsEnabled(fullTool.isEnabled);

        if (tool.toolType === "api_endpoint") {
          setApiUrl((config.url as string) ?? "");
          setApiMethod((config.method as string) ?? "POST");
          setHeadersJson(config.headers ? JSON.stringify(config.headers, null, 2) : "");
          setParametersJson(config.parameters ? JSON.stringify(config.parameters, null, 2) : "");
          setResponsePath((config.response_path as string) ?? "");
        } else if (tool.toolType === "mcp_server") {
          setMcpUrl((config.url as string) ?? "");
          setMcpHeadersJson(config.headers ? JSON.stringify(config.headers, null, 2) : "");
          setMcpTimeout((config.timeout as number) ?? 30);
        }
      } catch (err) {
        console.error("Failed to load tool:", err);
        showToast("Failed to load tool details", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchTool();
  }, [tool.uuid, tool.toolType, showToast]);

  const tryParseJson = (value: string): { valid: boolean; parsed: unknown } => {
    if (!value.trim()) return { valid: true, parsed: undefined };
    try {
      return { valid: true, parsed: JSON.parse(value) };
    } catch {
      return { valid: false, parsed: undefined };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast("Name is required", "warning");
      return;
    }

    try {
      setSaving(true);

      if (tool.toolType === "api_endpoint") {
        // Validate JSON fields
        const headersResult = tryParseJson(headersJson);
        const paramsResult = tryParseJson(parametersJson);

        if (!headersResult.valid) {
          setHeadersError("Invalid JSON");
          setSaving(false);
          return;
        }
        if (!paramsResult.valid) {
          setParametersError("Invalid JSON");
          setSaving(false);
          return;
        }

        setHeadersError("");
        setParametersError("");

        const config = {
          url: apiUrl,
          method: apiMethod,
          headers: headersResult.parsed,
          parameters: paramsResult.parsed,
          response_path: responsePath || undefined,
        };
        await toolService.update(tool.uuid, { name, description, config });
      } else if (tool.toolType === "mcp_server") {
        const mcpHeadersResult = tryParseJson(mcpHeadersJson);

        if (!mcpHeadersResult.valid) {
          setMcpHeadersError("Invalid JSON");
          setSaving(false);
          return;
        }

        setMcpHeadersError("");

        const config = {
          url: mcpUrl,
          headers: mcpHeadersResult.parsed,
          timeout: mcpTimeout || undefined,
        };
        await toolService.update(tool.uuid, { name, description, config });
      } else {
        await toolService.update(tool.uuid, { name, description, icon, isEnabled });
      }

      showToast("Tool updated successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to update tool:", err);
      showToast("Failed to update tool", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderApiEndpointFields = () => (
    <>
      <FormField label="URL" htmlFor="edit-tool-url" required>
        <FormInput
          id="edit-tool-url"
          value={apiUrl}
          onChange={setApiUrl}
          placeholder="https://api.example.com/endpoint"
        />
      </FormField>

      <FormField label="Method" htmlFor="edit-tool-method">
        <FormSelect
          id="edit-tool-method"
          value={apiMethod}
          onChange={setApiMethod}
          options={[
            { value: "GET", label: "GET" },
            { value: "POST", label: "POST" },
            { value: "PUT", label: "PUT" },
            { value: "DELETE", label: "DELETE" },
          ]}
        />
      </FormField>

      <FormField label="Headers (JSON)" htmlFor="edit-tool-headers" error={headersError}>
        <FormTextarea
          id="edit-tool-headers"
          value={headersJson}
          onChange={(v) => {
            setHeadersJson(v);
            setHeadersError("");
          }}
          placeholder={'{\n  "Authorization": "Bearer ...",\n  "Content-Type": "application/json"\n}'}
          rows={4}
          mono
        />
      </FormField>

      <FormField label="Parameters Schema (JSON)" htmlFor="edit-tool-parameters" error={parametersError}>
        <FormTextarea
          id="edit-tool-parameters"
          value={parametersJson}
          onChange={(v) => {
            setParametersJson(v);
            setParametersError("");
          }}
          placeholder={'{\n  "type": "object",\n  "properties": { ... }\n}'}
          rows={6}
          mono
        />
      </FormField>

      <FormField label="Response Path" htmlFor="edit-tool-response-path">
        <FormInput
          id="edit-tool-response-path"
          value={responsePath}
          onChange={setResponsePath}
          placeholder="data.result"
        />
      </FormField>
    </>
  );

  const renderMcpServerFields = () => (
    <>
      <FormField label="Server URL" htmlFor="edit-tool-mcp-url" required>
        <FormInput
          id="edit-tool-mcp-url"
          value={mcpUrl}
          onChange={setMcpUrl}
          placeholder="https://mcp-server.example.com"
        />
      </FormField>

      <FormField label="Headers (JSON)" htmlFor="edit-tool-mcp-headers" error={mcpHeadersError}>
        <FormTextarea
          id="edit-tool-mcp-headers"
          value={mcpHeadersJson}
          onChange={(v) => {
            setMcpHeadersJson(v);
            setMcpHeadersError("");
          }}
          placeholder={'{\n  "Authorization": "Bearer ..."\n}'}
          rows={4}
          mono
        />
      </FormField>

      <FormField label="Timeout (seconds)" htmlFor="edit-tool-mcp-timeout">
        <FormInput
          id="edit-tool-mcp-timeout"
          type="number"
          value={mcpTimeout}
          onChange={(v) => setMcpTimeout(v ? Number(v) : "")}
          placeholder="30"
          min={1}
        />
      </FormField>
    </>
  );

  const renderBuiltinFields = () => (
    <>
      <FormField label="Icon" htmlFor="edit-tool-icon">
        <FormInput id="edit-tool-icon" value={icon} onChange={setIcon} placeholder="wrench" />
      </FormField>

      <Toggle id="edit-tool-enabled" checked={isEnabled} onChange={setIsEnabled} label="Enabled" />
    </>
  );

  return (
    <FormModal
      title="Edit Tool"
      loading={loading}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={<FormActions onCancel={onClose} saving={saving} modal />}
    >
      <FormField label="Name" htmlFor="edit-tool-name" required>
        <FormInput id="edit-tool-name" value={name} onChange={setName} placeholder="Tool name" required />
      </FormField>

      <FormField label="Description" htmlFor="edit-tool-description">
        <FormTextarea
          id="edit-tool-description"
          value={description}
          onChange={setDescription}
          placeholder="What does this tool do?"
          rows={3}
        />
      </FormField>

      {tool.toolType === "api_endpoint" && renderApiEndpointFields()}
      {tool.toolType === "mcp_server" && renderMcpServerFields()}
      {tool.toolType !== "api_endpoint" && tool.toolType !== "mcp_server" && renderBuiltinFields()}
    </FormModal>
  );
}
